import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import api from 'apis/api';

import { useAuthStore } from 'store/index';
import { useWorkspaceStore } from 'store/workspace.store';

import type { Role } from 'types/auth';
import type { UserAccessAndRolesResponse } from 'types/auth/AuthResponses';

interface UseUserAccessAndRolesOptions {
  /** Control when the query is enabled (default: true when workspace is set) */
  enabled?: boolean;
}

export const useUserAccessAndRoles = (
  options: UseUserAccessAndRolesOptions = {}
) => {
  const { currentWorkspaceId } = useWorkspaceStore();
  const { user } = useAuthStore();
  const {
    setCurrentUserEntityRoleMap,
    setUsersEntityAccessList,
    setAllRoles,
    setUserAccessibleModules,
  } = useAuthStore();

  // Default enabled to true if workspace is set, but allow override
  const isEnabled = options.enabled ?? !!currentWorkspaceId;
  const queryClient = useQueryClient();

  const query = useQuery<UserAccessAndRolesResponse, Error>({
    queryKey: [
      'access-control-user-access-and-policies',
      currentWorkspaceId,
      user?.email,
    ],
    queryFn: async ({ queryKey }) => {
      // Get workspace ID from queryKey to avoid stale closure issues
      const workspaceId = queryKey[1] as string;
      const apiUrl = `/access-control/organization/${workspaceId}/user-access-and-policies`;

      // Skip loader on refetches (when cached data exists)
      const skipLoader = !!queryClient.getQueryData(queryKey);
      const { data } = await api.get<UserAccessAndRolesResponse>(apiUrl, {
        skipLoader,
      });

      // Grab current user's entity roles by email
      const userEntityAccess = data.users.find(
        (u) => u.user_email === user?.email
      )?.entity_access;

      const generatedEntityRolesMap = userEntityAccess?.reduce(
        (acc, entityAccess) => {
          acc[entityAccess.entity_id] = entityAccess.roles;
          return acc;
        },
        {} as Record<string, Role[]>
      );

      if (generatedEntityRolesMap) {
        setCurrentUserEntityRoleMap(generatedEntityRolesMap);
      }

      if (data.users) {
        setUsersEntityAccessList(data.users);
      }

      if (data.all_roles) {
        setAllRoles(data.all_roles);
      }

      return data;
    },
    enabled: isEnabled && !!currentWorkspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Fail closed: if the query fails after retries, set empty modules to deny access
  // This ensures PermissionGuard doesn't leave routes unprotected indefinitely
  useEffect(() => {
    if (query.isError) {
      setUserAccessibleModules([]);
    }
  }, [query.isError, setUserAccessibleModules]);

  return query;
};
